import { freeze } from '@/utils/object.js';

export const EVENT_BUS_EVENTS = freeze({
  PROHIBITED_CONTENT_OVERRIDDEN: 'prohibited_content_overridden',
  WAITING_ACTIVITY_ACCEPTED: 'waiting_activity_accepted',
  WAITING_ACTIVITY_REJECTED: 'waiting_activity_rejected',
});

export const PLATFORMS = freeze([
  {
    label: 'Facebook',
    machineName: 'facebook',
  },
  {
    label: 'Instagram',
    machineName: 'instagram',
  },
]);

export const META_PLATFORMS = freeze([ 'facebook', 'instagram' ]);

export const RULE_ACTION = freeze({
  START: 'start',
  STOP: 'stop',
  SCALING_UP: 'scaling_up',
  DELETE: 'delete',
});

export const ESTIMATED_SAVINGS_ACTIONS = freeze([
  {
    key: 'ad_policy_check',
    label: 'Ad policy check',
  },
  {
    key: 'start',
    label: 'Ad launch (original copy)',
  },
  {
    key: 'start_ai_creative',
    label: 'Ad launch (new copy)',
  },
  {
    key: 'scaling_up',
    label: 'Ad scaling',
  },
  {
    key: 'stop',
    label: 'Ad stop',
  },
]);

export const AI_STATUS = freeze({
  WAITING: 0,
  IN_PROGRESS: 1,
  DONE: 2,
  ERROR: 3,
  MANUAL: 4,
});

export const CONTENT_PUBLICATION_STATUS = freeze({
  SUCCESS: 1,
  ERROR: 2,
});

export const CAMPAIGN_STATUS = freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DELETED: 'deleted',
});

export const AD_CREATION_METHOD = freeze({
  POST_BOOST: 'post_boost',
  COPY: 'copy',
  CREATIVE: 'creative',
});

export const CTA_URL_USAGE = freeze({
  REQUIRED: 'required',
  OPTIONAL: 'optional',
  NOT_USED: 'not_used',
  APP_STORE: 'app_store',
});

export const AD_ACCOUNT_STATUS = freeze({
  ACTIVE: 1,
  DISABLED: 2,
  UNSETTLED: 3,
  PERMISSION_ERROR: 4,
  UNKNOWN: 5,
  PENDING_SETTLEMENT: 8,
});

export const AD_STATUS = freeze({
  ACTIVE: 1,
  PAUSED: 2,
  DISAPPROVED: 3,
  DELETED: 4,
});

export const AD_EFFECTIVE_STATUS = freeze({
  ACTIVE: 1,
  INACTIVE: 2,
  REJECTED: 6,
  WITH_ISSUES: 10,
});

export const AD_CREATIVE_TYPE = freeze({
  CUSTOM: 1,
  POST_BOOST: 2,
});

export const ORIGINAL_POST_LANGUAGE = 'original_post_language';
export const TARGETED_COUNTRY_LANGUAGE = 'targeted_country_language';

export const AI_ASSIST_OPTIONS = freeze({
  CREATE_CREATIVES: 'create_creatives',
});

export const RULE_RUN_LEVEL = freeze({
  AD: 'ad',
  ADSET: 'adset',
});

export const RULE_ACTIVITY_MANAGEMENT_INSTRUCTION = {
  ACCEPT: 'accept',
  REJECT: 'reject',
};

export const RULE_REVIEW_CODE = freeze({
  UNKNOWN: -1,
  WAITING_FOR_PROCESS: 0,
  WAITING_FOR_PROCESS_IN_ACTIVITY_MANAGEMENT: 17,
  IN_ACTIVITY_MANAGEMENT_QUEUE: 1,
  REJECTED_MANUALLY_IN_ACTIVITY_MANAGEMENT: 2,
  REJECTED_AUTOMATICALLY_IN_ACTIVITY_MANAGEMENT: 3,
  ALREADY_PROCESSED: 4,
  CONDITION_NOT_MET: 5,
  FILTERED_BY_POST_TYPE: 6,
  FILTERED_BY_POST_MEDIA_TYPE: 7,
  NOT_ENOUGH_BUDGET: 8,
  MAX_ACTIVE_ADS_LIMIT: 16,
  PROHIBITED_CONTENT: 9,
  EXPIRED_CONTENT: 10,
  VIDEO_AD_REQUIRES_VIDEO: 11,
  TRAFFIC_AND_CONVERSION_AD_REQUIRES_LINK: 12,
  ENGAGEMENT_CAMPAIGN_NOT_SUPPORTS_INSTAGRAM_ALBUM: 13,
  PAGE_LIKE_ENGAGEMENT_CAMPAIGN_NOT_SUPPORTS_ALBUM: 15,
  VIDEO_TOO_LONG: 14,
  MEDIA_RESTRICTED: 18,
  MEDIA_TOO_SMALL: 19,
  CREATIVE_FROM_STATUS_POST_NOT_SUPPORTED: 21,
  CTA_URL_MISSING_VALID_IMAGE: 22,
  CREATIVE_FROM_EVENT_POST_NOT_SUPPORTED: 23,
  TOO_MANY_ATTACHMENTS_FOR_BOOST: 24,
  POST_CONTAINS_COPYRIGHTED_MUSIC: 25,
  COLLABORATIVE_POST_NOT_BOOSTABLE: 26,
  ENGAGEMENT_CAMPAIGN_NOT_SUPPORTS_COLLABORATIVE_POST: 27,
  POST_DELETED: 28,
  SHARED_POST_NOT_BOOSTABLE: 29,
  CREATIVE_FROM_UNKNOWN_POST_NOT_SUPPORTED: 30,
});

export const RULE_REVIEW_CODE_TYPE = freeze({
  WAITING: 'waiting',
  COMPLETED: 'completed',
  FILTERED_OUT: 'filtered_out',
  CONTENT_ISSUE: 'content_issue',
  TECHNICAL_RESTRICTION: 'technical_restriction',
});

export const RULE_REVIEW_FILTER_DEFINITIONS = freeze([
  {
    code: RULE_REVIEW_CODE.UNKNOWN,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Post cannot be used',
      [RULE_ACTION.STOP]: 'Ad will not be stopped',
      [RULE_ACTION.SCALING_UP]: 'Ad cannot be scaled up',
    },
    label: {
      [RULE_ACTION.START]: 'This post cannot be used for advertising due to an unknown technical restriction',
      [RULE_ACTION.STOP]: 'This ad will not be stopped as the reason for processing cannot be determined',
      [RULE_ACTION.SCALING_UP]: 'This ad cannot be scaled up due to an unknown technical restriction',
    },
    whyNot: {
      [RULE_ACTION.START]: 'We encountered an unexpected technical issue that prevents using this post.',
      [RULE_ACTION.STOP]: 'We encountered an unexpected technical issue that prevents stopping this ad.',
      [RULE_ACTION.SCALING_UP]: 'We encountered an unexpected technical issue that prevents scaling up this ad.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Please try again later or contact support if the issue persists.',
      [RULE_ACTION.STOP]: 'Please try again later or contact support if the issue persists.',
      [RULE_ACTION.SCALING_UP]: 'Please try again later or contact support if the issue persists.',
    },
  },
  {
    code: RULE_REVIEW_CODE.WAITING_FOR_PROCESS,
    type: RULE_REVIEW_CODE_TYPE.WAITING,
    shortLabel: {
      [RULE_ACTION.START]: 'Ad will be created',
      [RULE_ACTION.STOP]: 'Ad will be stopped',
      [RULE_ACTION.SCALING_UP]: 'Ad will be scaled up',
    },
    label: {
      [RULE_ACTION.START]: 'This post is scheduled to be processed for ad creation',
      [RULE_ACTION.STOP]: 'This ad is scheduled to be processed for deactivation',
      [RULE_ACTION.SCALING_UP]: 'This ad is scheduled to be processed for scaling up',
    },
  },
  {
    code: RULE_REVIEW_CODE.WAITING_FOR_PROCESS_IN_ACTIVITY_MANAGEMENT,
    type: RULE_REVIEW_CODE_TYPE.WAITING,
    shortLabel: {
      [RULE_ACTION.START]: 'Ad will be created',
      [RULE_ACTION.STOP]: 'Ad will be stopped',
      [RULE_ACTION.SCALING_UP]: 'Ad will be scaled up',
    },
    label: {
      [RULE_ACTION.START]: 'This post is scheduled to be processed for ad creation',
      [RULE_ACTION.STOP]: 'This ad is scheduled to be processed for deactivation',
      [RULE_ACTION.SCALING_UP]: 'This ad is scheduled to be processed for scaling up',
    },
  },
  {
    code: RULE_REVIEW_CODE.IN_ACTIVITY_MANAGEMENT_QUEUE,
    type: RULE_REVIEW_CODE_TYPE.WAITING,
    shortLabel: {
      [RULE_ACTION.START]: 'Waiting for approval',
      [RULE_ACTION.STOP]: 'Waiting for approval',
      [RULE_ACTION.SCALING_UP]: 'Waiting for approval',
    },
    label: {
      [RULE_ACTION.START]: 'This post is currently in the queue for ad creation',
      [RULE_ACTION.STOP]: 'This ad is currently in the queue for deactivation',
      [RULE_ACTION.SCALING_UP]: 'This ad is currently in the queue for scaling up',
    },
    whyNot: {
      [RULE_ACTION.START]: 'You have enabled activity management in the automation, and the ad creation is now waiting for your approval.',
      [RULE_ACTION.STOP]: 'You have enabled activity management in the automation, and stopping the ad is now only waiting for your approval.',
      [RULE_ACTION.SCALING_UP]: 'You have enabled activity management in the automation, and the ad creation is now waiting for your approval.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Accept or reject the activity.',
      [RULE_ACTION.STOP]: 'Accept or reject the activity.',
      [RULE_ACTION.SCALING_UP]: 'Accept or reject the activity.',
    },
  },
  {
    code: RULE_REVIEW_CODE.REJECTED_MANUALLY_IN_ACTIVITY_MANAGEMENT,
    type: RULE_REVIEW_CODE_TYPE.FILTERED_OUT,
    shortLabel: {
      [RULE_ACTION.START]: 'Manually rejected',
      [RULE_ACTION.STOP]: 'Manually rejected',
      [RULE_ACTION.SCALING_UP]: 'Manually rejected',
    },
    label: {
      [RULE_ACTION.START]: 'Ad creation manually rejected',
      [RULE_ACTION.STOP]: 'Ad stop manually rejected',
      [RULE_ACTION.SCALING_UP]: 'Ad creation manually rejected',
    },
    whyNot: {
      [RULE_ACTION.START]: 'You enabled activity management in the automation and manually rejected the ad creation.',
      [RULE_ACTION.STOP]: 'In the automation, you enabled activity management and manually rejected the ad stoppage.',
      [RULE_ACTION.SCALING_UP]: 'You enabled activity management in the automation and manually rejected the ad creation.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you have changed your mind, you can still accept the activity afterwards.',
      [RULE_ACTION.STOP]: 'If you have changed your mind, you can still accept the activity afterwards.',
      [RULE_ACTION.SCALING_UP]: 'If you have changed your mind, you can still accept the activity afterwards.',
    },
  },
  {
    code: RULE_REVIEW_CODE.REJECTED_AUTOMATICALLY_IN_ACTIVITY_MANAGEMENT,
    type: RULE_REVIEW_CODE_TYPE.FILTERED_OUT,
    shortLabel: {
      [RULE_ACTION.START]: 'Automatically rejected',
      [RULE_ACTION.STOP]: 'Automatically rejected',
      [RULE_ACTION.SCALING_UP]: 'Automatically rejected',
    },
    label: {
      [RULE_ACTION.START]: 'Ad creation automatically rejected',
      [RULE_ACTION.STOP]: 'Ad stop automatically rejected',
      [RULE_ACTION.SCALING_UP]: 'Ad creation automatically rejected',
    },
    whyNot: {
      [RULE_ACTION.START]: 'You enabled activity management in the automation and automatically rejected the ad creation.',
      [RULE_ACTION.STOP]: 'In the automation, you enabled activity management, and the ad stop was automatically rejected.',
      [RULE_ACTION.SCALING_UP]: 'You enabled activity management in the automation and automatically rejected the ad creation.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you have changed your mind, you can still accept the activity afterwards.',
      [RULE_ACTION.STOP]: 'If you have changed your mind, you can still accept the activity afterwards.',
      [RULE_ACTION.SCALING_UP]: 'If you have changed your mind, you can still accept the activity afterwards.',
    },
  },
  {
    code: RULE_REVIEW_CODE.ALREADY_PROCESSED,
    type: RULE_REVIEW_CODE_TYPE.COMPLETED,
    shortLabel: {
      [RULE_ACTION.START]: 'Ad already created',
      [RULE_ACTION.STOP]: 'Ad already stopped',
      [RULE_ACTION.SCALING_UP]: 'Ad already scaled up',
    },
    label: {
      [RULE_ACTION.START]: 'An ad has already been created from this post',
      [RULE_ACTION.STOP]: 'This ad has already been deactivated',
      [RULE_ACTION.SCALING_UP]: 'This ad has already been scaled up',
    },
  },
  {
    code: RULE_REVIEW_CODE.CONDITION_NOT_MET,
    type: RULE_REVIEW_CODE_TYPE.FILTERED_OUT,
    shortLabel: {
      [RULE_ACTION.START]: 'Post excluded by filters',
      [RULE_ACTION.STOP]: 'Ad excluded by filters',
      [RULE_ACTION.SCALING_UP]: 'Ad excluded by filters',
    },
    label: {
      [RULE_ACTION.START]: 'This post does not match the selection criteria set for ad creation in the automation',
      [RULE_ACTION.STOP]: 'This ad does not match the selection criteria set for deactivation in the automation',
      [RULE_ACTION.SCALING_UP]: 'This ad does not match the selection criteria set for scaling up in the automation',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Your automation is set up to create ads only from posts that match specific criteria. This post does not meet these criteria.',
      [RULE_ACTION.STOP]: 'Your automation is set up to stop only ads that match specific criteria. This ad does not meet these criteria.',
      [RULE_ACTION.SCALING_UP]: 'Your automation is set up to scale up only ads that match specific criteria. This ad does not meet these criteria.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Review and adjust your automation filters if you want this post to be included.',
      [RULE_ACTION.STOP]: 'Review and adjust your automation filters if you want this ad to be included.',
      [RULE_ACTION.SCALING_UP]: 'Review and adjust your automation filters if you want this ad to be included.',
    },
  },
  {
    code: RULE_REVIEW_CODE.EXPIRED_CONTENT,
    type: RULE_REVIEW_CODE_TYPE.CONTENT_ISSUE,
    shortLabel: {
      [RULE_ACTION.START]: 'Post content expired',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Ad content expired',
    },
    label: {
      [RULE_ACTION.START]: 'This post has expired and cannot be used for creating new ads',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'This ad contains expired content and cannot be scaled up',
    },
    whyNot: {
      [RULE_ACTION.START]: 'The content in this post has an expiration date that has already passed. We cannot create ads from expired content.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'The content used in this ad has expired. We cannot scale up ads with expired content.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you disagree with the expiration assessment, you can edit the content expiration date in the post settings. Alternatively, you can disable the "Do not create ads from posts with expired content" AI Assist feature if you want to proceed with this ad.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'If you disagree with the expiration assessment, you can edit the content expiration date in the post settings. Alternatively, you can disable the "Do not scale up ads with expired content" AI Assist feature if you want to proceed.',
    },
  },
  {
    code: RULE_REVIEW_CODE.PROHIBITED_CONTENT,
    type: RULE_REVIEW_CODE_TYPE.CONTENT_ISSUE,
    shortLabel: {
      [RULE_ACTION.START]: 'Prohibited content',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post contains prohibited content and cannot be used for advertising',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'The content in this post does not comply with Meta\'s advertising policies. This might include restricted products, services, or other content prohibited by Meta.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you disagree with this assessment, you can override the AI\'s decision using the button below.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.FILTERED_BY_POST_TYPE,
    type: RULE_REVIEW_CODE_TYPE.FILTERED_OUT,
    shortLabel: {
      [RULE_ACTION.START]: 'Post excluded by filters',
      [RULE_ACTION.STOP]: 'Ad excluded by filters',
      [RULE_ACTION.SCALING_UP]: 'Ad excluded by filters',
    },
    label: {
      [RULE_ACTION.START]: 'This post type is excluded from ad creation by the automation settings',
      [RULE_ACTION.STOP]: 'This post type is excluded from ad deactivation by the automation settings',
      [RULE_ACTION.SCALING_UP]: 'This post type is excluded from ad scaling up by the automation settings',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Your automation is set up to handle only specific types of posts (e.g., photos, videos, links). This post type is not included.',
      [RULE_ACTION.STOP]: 'Your automation is set up to handle only specific types of posts. This ad\'s post type is not included.',
      [RULE_ACTION.SCALING_UP]: 'Your automation is set up to handle only specific types of posts. This ad\'s post type is not included.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Include this post type in your automation settings if you want it to be processed.',
      [RULE_ACTION.STOP]: 'Include this post type in your automation settings if you want it to be processed.',
      [RULE_ACTION.SCALING_UP]: 'Include this post type in your automation settings if you want it to be processed.',
    },
  },
  {
    code: RULE_REVIEW_CODE.FILTERED_BY_POST_MEDIA_TYPE,
    type: RULE_REVIEW_CODE_TYPE.FILTERED_OUT,
    shortLabel: {
      [RULE_ACTION.START]: 'Post excluded by filters',
      [RULE_ACTION.STOP]: 'Ad excluded by filters',
      [RULE_ACTION.SCALING_UP]: 'Ad excluded by filters',
    },
    label: {
      [RULE_ACTION.START]: 'This post media type is excluded from ad creation by the automation settings',
      [RULE_ACTION.STOP]: 'This post media type is excluded from ad deactivation by the automation settings',
      [RULE_ACTION.SCALING_UP]: 'This post media type is excluded from ad scaling up by the automation settings',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Your automation is set up to handle only specific media types. The media in this post is not included.',
      [RULE_ACTION.STOP]: 'Your automation is set up to handle only specific media types. The media in this ad\'s post is not included.',
      [RULE_ACTION.SCALING_UP]: 'Your automation is set up to handle only specific media types. The media in this ad\'s post is not included.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Include this media type in your automation settings if you want it to be processed.',
      [RULE_ACTION.STOP]: 'Include this media type in your automation settings if you want it to be processed.',
      [RULE_ACTION.SCALING_UP]: 'Include this media type in your automation settings if you want it to be processed.',
    },
  },
  {
    code: RULE_REVIEW_CODE.NOT_ENOUGH_BUDGET,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Insufficient budget',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Insufficient budget',
    },
    label: {
      [RULE_ACTION.START]: 'There is not enough campaign budget to create an ad from this post',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'There is not enough campaign budget to scale up this ad',
    },
    whyNot: {
      [RULE_ACTION.START]: 'The campaign\'s current budget is too low to create new ads.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'The target campaign\'s budget is too low to add this ad.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Increase your campaign budget if you want this ad to be created.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Increase your campaign budget if you want this ad to be scaled up.',
    },
  },
  {
    code: RULE_REVIEW_CODE.MAX_ACTIVE_ADS_LIMIT,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Max active ads limit reached',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Max active ads limit reached',
    },
    label: {
      [RULE_ACTION.START]: 'An ad will not be created from this post because the maximum active ads limit you set has been reached',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'This ad will not be scaled up because the maximum active ads limit you set has been reached',
    },
    whyNot: {
      [RULE_ACTION.START]: 'You have set a maximum number of active ads in the Adset budget settings, and this limit has been reached.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'You have set a maximum number of active ads in the Adset budget settings, and this limit has been reached.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Increase the maximum active ads limit in your Adset budget settings if you want this ad to be created.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Increase the maximum active ads limit in your Adset budget settings if you want this ad to be scaled up.',
    },
  },
  {
    code: RULE_REVIEW_CODE.VIDEO_AD_REQUIRES_VIDEO,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'No video content',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'No video content',
    },
    label: {
      [RULE_ACTION.START]: 'This post cannot be used for video ads as it does not contain video content',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'This ad cannot be scaled up as a video ad because the original post does not contain video content',
    },
    whyNot: {
      [RULE_ACTION.START]: 'For this campaign objective, only posts containing video content can be used to create ads.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'For this campaign objective, only ads created from posts containing video content can be scaled up.',
    },
  },
  {
    code: RULE_REVIEW_CODE.TRAFFIC_AND_CONVERSION_AD_REQUIRES_LINK,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'No link or CTA URL',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'No link provided',
    },
    label: {
      [RULE_ACTION.START]: 'This post cannot be used for traffic or conversion ads as it does not contain a link and no CTA URL is set',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'This ad cannot be scaled up for traffic or conversion as the original post does not contain a link',
    },
    whyNot: {
      [RULE_ACTION.START]: 'For traffic and conversion campaign objectives, the ad needs a clickable destination: either the post is a link post, or a CTA URL is set. A URL that only appears in the post text is not clickable in the ad.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'For traffic and conversion campaign objectives, only ads created from posts containing a clickable link can be scaled up.',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'Set a CTA URL in the Call to action block of the rule; it will be used as the destination of the ad.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.ENGAGEMENT_CAMPAIGN_NOT_SUPPORTS_INSTAGRAM_ALBUM,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Unsupported format',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Unsupported format',
    },
    label: {
      [RULE_ACTION.START]: 'This Instagram album post cannot be used for engagement campaign ads',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Instagram album posts cannot be used for engagement campaign ads due to platform limitations.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.PAGE_LIKE_ENGAGEMENT_CAMPAIGN_NOT_SUPPORTS_ALBUM,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Unsupported format',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Unsupported format',
    },
    label: {
      [RULE_ACTION.START]: 'This album post cannot be used for page like engagement campaign ads',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Album posts cannot be used for page like engagement campaign ads due to platform limitations.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.VIDEO_TOO_LONG,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Video too long',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Video too long',
    },
    label: {
      [RULE_ACTION.START]: 'The video in this post exceeds the maximum allowed duration of 300 seconds for advertising',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'The video in the original post exceeds the maximum allowed duration of 300 seconds for advertising',
    },
    whyNot: {
      [RULE_ACTION.START]: 'The :app platform only accepts videos up to 300 seconds (5 minutes) in length.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.MEDIA_RESTRICTED,
    type: RULE_REVIEW_CODE_TYPE.CONTENT_ISSUE,
    shortLabel: {
      [RULE_ACTION.START]: 'Restricted media',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post contains restricted media and cannot be used for advertising',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'The content in this post contains restricted media that does not comply with Meta\'s advertising policies.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.MEDIA_TOO_SMALL,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Media too small',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'The media in this post is too small for advertising',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Instagram ads require images to be at least 500px wide. Since the media in this post does not reach this minimum size, it cannot be used to create an ad.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.CREATIVE_FROM_STATUS_POST_NOT_SUPPORTED,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Unsupported format',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Unsupported format',
    },
    label: {
      [RULE_ACTION.START]: 'Text-only posts cannot be used for ad creatives',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Text-only posts (lacking image, video, or link) cannot be converted into ad creatives due to platform limitations.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you want to advertise this post, please use the "Boosting original posts" ad creation method instead.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.CTA_URL_MISSING_VALID_IMAGE,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'No visual content',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post cannot be used because it has no image or video, and the CTA link does not provide a usable image.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Facebook requires a visual (image or video) to create an ad. This post is text-only, and the CTA link does not contain a usable image either.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'You can change the CTA link to a page that contains a usable image, or switch to the "Boosting original posts" ad creation method to create the ad.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.CREATIVE_FROM_EVENT_POST_NOT_SUPPORTED,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Unsupported format',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'Event posts cannot be used for ad creatives',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Event posts cannot be converted into ad creatives due to platform limitations.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you want to advertise this post, please use the "Boosting original posts" ad creation method instead.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.TOO_MANY_ATTACHMENTS_FOR_BOOST,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Too many media items',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post contains too many media items to be boosted',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'When boosting a post, each media item becomes a separate carousel card in the ad. Facebook only allows up to 10 cards per ad, but this post contains more media items than that.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you still want to advertise this post, please use the "Creating ad creative" ad creation method instead, as this limit does not apply there.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.POST_CONTAINS_COPYRIGHTED_MUSIC,
    type: RULE_REVIEW_CODE_TYPE.CONTENT_ISSUE,
    shortLabel: {
      [RULE_ACTION.START]: 'Copyrighted music',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post contains copyrighted music and cannot be boosted',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'Meta detected copyrighted music in this post. Posts that include copyrighted music cannot be boosted as ads.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you still want to advertise this post, please use the "Creating ad creative" ad creation method instead, as this limit does not apply there.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.COLLABORATIVE_POST_NOT_BOOSTABLE,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Collaborative post',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post was published by another page and cannot be boosted',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'This is a collaborative post, published by another page and shown on the feed of every author. Meta only lets the publishing page boost such a post.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you still want to advertise this post, please use the "Creating ad creative" ad creation method instead, as this limit does not apply there.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.ENGAGEMENT_CAMPAIGN_NOT_SUPPORTS_COLLABORATIVE_POST,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Collaborative post',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post was published by another page, so no engagement ad can run on it',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'This is a collaborative post, published by another page and shown on the feed of every author. Meta only lets the publishing page boost such a post, and an engagement campaign collects the engagement on the boosted post itself.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: '',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.POST_DELETED,
    type: RULE_REVIEW_CODE_TYPE.CONTENT_ISSUE,
    shortLabel: {
      [RULE_ACTION.START]: '',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Original post deleted',
    },
    label: {
      [RULE_ACTION.START]: '',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'The original post of this ad has been deleted, so the ad cannot be scaled up',
    },
    whyNot: {
      [RULE_ACTION.START]: '',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'Scaling up works by copying the ad, and Meta only allows that while the post behind the ad still exists. This post has been deleted from the page, so Meta rejects every new ad based on it, and the ads already running on it have been stopped.',
    },
    whatToDo: {
      [RULE_ACTION.START]: '',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: 'A deleted post cannot be advertised again, so there is nothing to fix on this ad. If the post was removed by mistake, publish it again: your ad creation automation can then start a new ad from it.',
    },
  },
  {
    code: RULE_REVIEW_CODE.SHARED_POST_NOT_BOOSTABLE,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Shared post',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'This post shares content published by someone else and cannot be boosted',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'The content of this post was published by another page or profile, and your page only shared it. Meta lets only the original publisher boost such content.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you still want to advertise this content, please use the "Creating ad creative" ad creation method instead, as this limit does not apply there. Publishing the content as your own post also makes it boostable.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
  {
    code: RULE_REVIEW_CODE.CREATIVE_FROM_UNKNOWN_POST_NOT_SUPPORTED,
    type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
    shortLabel: {
      [RULE_ACTION.START]: 'Unknown content',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    label: {
      [RULE_ACTION.START]: 'No ad creative can be built from this post, because the type of its content cannot be determined',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whyNot: {
      [RULE_ACTION.START]: 'This post has no image or video of its own: it shows a shared post, an event or a group post, whose details are not available for ad creation. Boosting the post itself is not affected by this.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
    whatToDo: {
      [RULE_ACTION.START]: 'If you want to advertise this post, please use the "Boosting original posts" ad creation method instead: that advertises the post itself, so it does not need the attached content.',
      [RULE_ACTION.STOP]: '',
      [RULE_ACTION.SCALING_UP]: '',
    },
  },
]);

export const RULE_ACTIVITY_REASON = freeze({
  EXPIRED_CONTENT: 1,
  MAX_ADS_BY_USER: 2,
  MAX_ADS_BY_CAMPAIGN_BUDGET: 3,
  MAX_ADS_TECHNICAL_LIMIT: 4,
  MAX_ADS_TECHNICAL_LIMIT_CBO: 5,
  ERROR_FIX_NOT_DELIVERING: 6,
  ERROR_FIX_REMOVED_OBJECT: 7,
});

export const RULE_ACTIVITY_REASON_DEFINITIONS = freeze({
  [RULE_ACTIVITY_REASON.EXPIRED_CONTENT]: {
    title: 'Content out of date',
    description: 'The ad was stopped because its post content is no longer up to date.',
  },
  [RULE_ACTIVITY_REASON.MAX_ADS_BY_USER]: {
    title: 'Maximum number of ads reached',
    description: 'The ad was stopped because the campaign reached the maximum number of active ads you set.',
  },
  [RULE_ACTIVITY_REASON.MAX_ADS_BY_CAMPAIGN_BUDGET]: {
    title: 'Campaign budget limit reached',
    description: 'The ad was stopped because the campaign budget cannot fund any more active ads.',
  },
  [RULE_ACTIVITY_REASON.MAX_ADS_TECHNICAL_LIMIT]: {
    title: 'Platform ad limit reached',
    description: 'The ad was stopped because the campaign reached the maximum number of ads allowed by the platform.',
  },
  [RULE_ACTIVITY_REASON.MAX_ADS_TECHNICAL_LIMIT_CBO]: {
    title: 'Platform ad limit reached (CBO)',
    description: 'The ad was stopped because this Campaign Budget campaign reached the maximum number of ads allowed by the platform.',
  },
  [RULE_ACTIVITY_REASON.ERROR_FIX_NOT_DELIVERING]: {
    title: 'Not delivering',
    description: 'The ad was removed automatically because it got stuck in a "Not delivering" state.',
  },
  [RULE_ACTIVITY_REASON.ERROR_FIX_REMOVED_OBJECT]: {
    title: 'Post moved to trash',
    description: 'The ad was removed automatically because its post was moved to the trash.',
  },
});

export const GOAL_OPTIONS = freeze([
  { value: 'traffic', icon: 'traffic', title: 'I want to drive traffic to my website', description: 'You have a website and want to attract visitors using ads' },
  { value: 'conversion', icon: 'conversion', title: 'I want to increase my sales', description: 'You have a webshop or sell products/services online' },
  { value: 'app_promotion', icon: 'promotion', title: 'I want to promote my app', description: 'You have an app and want it to achieve better results' },
  { value: 'engagement', icon: 'engagement', title: 'I want more reach and engagement on my posts', description: 'You want your Facebook or Instagram posts to get more reactions and reach' },
  { value: 'page_like', icon: 'pagelike', title: 'I want to increase my number of Facebook followers', description: 'You have a Facebook page and want to gain more followers with your content' },
  { value: 'leads', icon: 'funnel', title: 'I want to collect leads', description: 'You want to collect contact information from potential customers using lead generation forms' },
]);

export const GENDER_ALL = 'all';
export const GENDER_MEN = 'men';
export const GENDER_WOMEN = 'women';
export const GENDERS = freeze([
  { value: GENDER_ALL, label: 'All' },
  { value: GENDER_MEN, label: 'Men' },
  { value: GENDER_WOMEN, label: 'Women' },
]);

export const CONTENT_GENERATION_ITEM_CONTENT_LENGTHS = freeze([
  { value: 'short', label: 'Short - Quick message (50-300 characters)' },
  { value: 'standard', label: 'Standard - Typical post (300-500 characters)' },
  { value: 'long', label: 'Long - Detailed content (500-1000 characters)' },
  { value: 'very_long', label: 'Very long - Educational/story (1000+ characters)' },
]);
